import { useEffect, useState } from 'react';
import { ArrowRight, Copy, Download, Pause, Play, Save, Upload } from 'lucide-react';
import { screenplaysApi, SCREENPLAY_IMAGE_BYTES, screenplayCanGenerateCharacter, screenplayEpisodeProductNames, screenplayReferenceDirectory,
  screenplayReferenceProgress, screenplayStoredReferences, validateScreenplaySelections, type ScreenplayAsset, type ScreenplayMatch,
  type ScreenplayRecord, type ScreenplayReferenceImage, type ScreenplaySelection } from '../services/screenplays';

export function ScreenplayOriginalImage({ filmId, asset }: { filmId: string; asset: ScreenplayAsset }) {
  const [url, setUrl] = useState('');
  const [error, setError] = useState(false);
  useEffect(() => {
    const controller = new AbortController(); let objectUrl = '';
    setUrl(''); setError(false);
    void screenplaysApi.original(filmId, asset.id, controller.signal).then(blob => {
      if (controller.signal.aborted) return;
      if (!['image/png', 'image/jpeg', 'image/webp'].includes(blob.type) || blob.type !== asset.mime
        || blob.size < 1 || blob.size > SCREENPLAY_IMAGE_BYTES || blob.size !== asset.size) throw new Error('Invalid original image.');
      objectUrl = URL.createObjectURL(blob); setUrl(objectUrl);
    }).catch(() => { if (!controller.signal.aborted) setError(true); });
    return () => { controller.abort(); if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [filmId, asset.id, asset.mime, asset.size]);
  return <figure className="screenplay-original">
    {url ? <img src={url} alt={`Original ${asset.name}: ${asset.relativePath}`} onError={() => setError(true)} />
      : <div className="screenplay-image-placeholder" role="status">{error ? 'Original image unavailable' : 'Loading original'}</div>}
    {url && error && <span className="character-film-error" role="status">Original image unavailable</span>}
    <figcaption>{asset.relativePath}</figcaption>
  </figure>;
}

function CopyReferenceValue({ label, value }: { label: string; value: string }) {
  const [status, setStatus] = useState('');
  return <span className="screenplay-copy"><button type="button" className="screenplay-icon" title={`Copy ${label}`} aria-label={`Copy ${label}`}
    onClick={() => { void navigator.clipboard.writeText(value).then(() => setStatus('Copied')).catch(() => setStatus('Copy unavailable')); }}><Copy size={14} /></button>
    {status && <span role="status">{status}</span>}</span>;
}

function ScreenplayStoredReference({ filmId, folder, reference }: { filmId: string; folder: string; reference: ScreenplayReferenceImage }) {
  const [url, setUrl] = useState(''), [error, setError] = useState(false);
  useEffect(() => {
    const controller = new AbortController(); let objectUrl = '';
    setUrl(''); setError(false);
    void screenplaysApi.referenceImage(filmId, reference.entityId, controller.signal).then(blob => {
      if (controller.signal.aborted) return;
      if (!['image/png', 'image/jpeg', 'image/webp'].includes(blob.type) || blob.type !== reference.mime
        || blob.size < 1 || blob.size > SCREENPLAY_IMAGE_BYTES || blob.size !== reference.size) throw new Error('Invalid stored image.');
      objectUrl = URL.createObjectURL(blob); setUrl(objectUrl);
    }).catch(() => { if (!controller.signal.aborted) setError(true); });
    return () => { controller.abort(); if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [filmId, reference.entityId, reference.mediaId, reference.sha256, reference.mime, reference.size]);
  return <figure className="screenplay-original screenplay-stored-reference" data-source={reference.source}>
    {url && !error ? <img src={url} alt={`Stored ${reference.source} reference: ${reference.entityName}`} onError={() => setError(true)} />
      : <div className="screenplay-image-placeholder" role="status">{error ? 'Stored image unavailable' : 'Loading stored image'}</div>}
    <figcaption><strong>{reference.entityName}</strong><span> / {reference.source}</span>
      <code className="screenplay-id" title={`${folder.replace(/\/+$/u, '')}/${reference.relativePath}`}>{reference.relativePath}</code>
      <div className="screenplay-reference-actions"><code title={reference.sha256}>{reference.sha256.slice(0, 12)}...</code>
        <CopyReferenceValue label={`${reference.entityName} reference SHA256`} value={reference.sha256} />
        {url && !error && <a className="screenplay-icon" href={url} download={reference.relativePath.split('/').pop()}
          title={`Download ${reference.entityName} reference image`} aria-label={`Download ${reference.entityName} reference image`}><Download size={16} /></a>}
      </div></figcaption>
  </figure>;
}

export function ScreenplayProgressReferences({ record }: { record: ScreenplayRecord }) {
  const progress = screenplayReferenceProgress(record), files = screenplayStoredReferences(record);
  const directory = screenplayReferenceDirectory(record);
  return <section className="screenplay-band" aria-label="Stored reference images"><h3>References</h3>
    <p>{progress.completed} / {progress.total} canonical references stored</p>
    <p className="character-film-muted">{progress.stored} / {progress.expected} reference files stored</p>
    {progress.verified && <p role="status">Reference set verified</p>}
    {directory && <div className="screenplay-folder"><span>Character image folder</span><code title={record.characterFolder}>{record.characterFolder}</code>
      <CopyReferenceValue label="character image folder" value={record.characterFolder} /></div>}
    {progress.verified && <div className="screenplay-folder"><span>Reference directory</span><code title={directory!}>{directory}</code>
      <CopyReferenceValue label="reference directory" value={directory!} /></div>}
    <ul className="screenplay-lines">{record.plan?.entities.filter(entity => !entity.voiceOnly).map(entity => {
      const saved = record.execution?.entities?.find(item => item.name === entity.name);
      const stored = files.find(reference => reference.entityId === saved?.id);
      const product = record.plan?.products.find(item => item.entityName === entity.name);
      const original = record.selections?.some(item => item.name === (product?.name ?? entity.name)
        && item.kind === (product ? 'product' : 'character') && item.assetIds.length);
      return <li key={entity.name}><strong>{entity.name}</strong><span> / {stored ? `${stored.source} file stored`
        : saved?.mediaId ? 'Media assigned / file not stored' : original ? 'Planned original reference' : 'Planned generated reference'}</span>
        {saved?.mediaId && <code className="screenplay-id">{saved.mediaId}</code>}</li>;
    })}</ul>
    <div className="screenplay-image-grid screenplay-stored-grid">{files.map(reference =>
      <ScreenplayStoredReference key={`${record.id}:${reference.entityId}`} filmId={record.id} folder={record.characterFolder} reference={reference} />)}</div>
  </section>;
}

function ScreenplayMatchChoices({ record, match, selections, disabled, onChange }: {
  record: ScreenplayRecord; match: ScreenplayMatch; selections: ScreenplaySelection[]; disabled: boolean;
  onChange: (selections: ScreenplaySelection[]) => void;
}) {
  const [folderChoice, setFolderChoice] = useState<string | null>(null);
  const selection = selections.find(item => item.name === match.name && item.kind === match.kind);
  const selected = selection?.assetIds ?? [], canGenerate = screenplayCanGenerateCharacter(record, match);
  const allowed = new Set([...match.assetIds, ...(match.kind === 'product' ? match.candidateAssetIds ?? [] : [])]);
  const candidates = (record.assets ?? []).filter(asset => allowed.has(asset.id) && asset.kind === match.kind);
  const groups = new Map<string, ScreenplayAsset[]>();
  for (const asset of candidates) groups.set(asset.name, [...groups.get(asset.name) ?? [], asset]);
  const selectedFolder = candidates.find(asset => selected.includes(asset.id))?.name;
  const activeFolder = selectedFolder ?? folderChoice ?? '';
  const visible = match.kind === 'product' ? groups.get(activeFolder) ?? [] : candidates;
  const change = (assetIds: string[], generate?: true) => onChange([
    ...selections.filter(item => item.kind !== match.kind || item.name !== match.name),
    ...(assetIds.length || generate ? [{ kind: match.kind, name: match.name, assetIds, ...(generate ? { generate } : {}) }] : []),
  ]);
  return <fieldset className="screenplay-match" disabled={disabled}>
    <legend>{match.name} <span className="character-film-muted">/ {match.kind} / {match.status}</span></legend>
    {canGenerate ? <>
      <label className="screenplay-generation-mode"><input type="checkbox" aria-label={`Generate ${match.name} from approved description`}
        checked={selection?.generate === true} onChange={event => change([], event.target.checked ? true : undefined)} />Generate from approved description</label>
      <p className="screenplay-prose">{record.analysis?.characters.find(character => character.name === match.name)?.description}</p>
      <p className="character-film-muted" role="status">{selection?.generate ? 'Planned reference / not generated' : 'No reference mode selected'}</p>
    </> : <>
      {match.kind === 'product' && <>
        <label>Product folder for {match.name}<select aria-label={`Product folder for ${match.name}`} value={activeFolder}
          title="Explicitly bind this screenplay product to one catalog folder. Different names are not matched automatically."
          onChange={event => {
            const folder = event.target.value, images = groups.get(folder) ?? [];
            setFolderChoice(folder); change(images.slice(0, images.length <= 6 ? 6 : 1).map(asset => asset.id));
          }}><option value="">None selected</option>{[...groups].map(([name, images]) =>
            <option key={name} value={name}>{name} ({images.length} images)</option>)}</select></label>
        {activeFolder && activeFolder !== match.name && <p className="character-film-muted">Explicit binding: {match.name} / {activeFolder}</p>}
        <p className="character-film-muted">{selected.length} / 6 product views selected{visible.length > 6 ? ' / Choose views' : ''}</p>
      </>}
      {!visible.length && <p className="character-film-error" role="status">Missing image: {match.name}</p>}
      <div className="screenplay-image-grid">{visible.map(asset => <label className="screenplay-choice" key={asset.id}>
        <input type={match.kind === 'character' ? 'radio' : 'checkbox'} name={`image-${record.id}-${match.kind}-${match.name}`}
          aria-label={`Select ${match.name} image ${asset.relativePath}`} checked={selected.includes(asset.id)}
          disabled={disabled || (match.kind === 'product' && selected.length >= 6 && !selected.includes(asset.id))}
          onChange={event => {
            if (match.kind === 'product') setFolderChoice(activeFolder);
            change(match.kind === 'character' ? [asset.id] : event.target.checked
              ? [...selected, asset.id] : selected.filter(id => id !== asset.id));
          }} />
        <ScreenplayOriginalImage filmId={record.id} asset={asset} />
      </label>)}</div>
    </>}
  </fieldset>;
}

export default function ScreenplayReferences({
  record, selections, dirty, disabled, prepareReason,
  resumeReason, pauseReason, runReason,
  onChange, onSave, onPrepare, onResume, onPause, onRun, onNavigate,
}: {
  record: ScreenplayRecord; selections: ScreenplaySelection[]; dirty: boolean; disabled: boolean;
  prepareReason: string; resumeReason?: string; pauseReason?: string; runReason?: string;
  onChange: (selections: ScreenplaySelection[]) => void;
  onSave: () => void;
  onPrepare: () => void;
  onResume?: () => void;
  onPause?: () => void;
  onRun?: () => void;
  onNavigate?: (view: 'inputs' | 'references' | 'plan' | 'progress' | 'rules' | 'dashboard') => void;
}) {
  let invalid = '';
  try { validateScreenplaySelections(record, selections); }
  catch (failure) { invalid = failure instanceof Error ? failure.message : 'Invalid image selections.'; }

  const hasPlan = !!record.plan;
  const isPaused = record.task && ['paused', 'failed'].includes(record.task.state);
  const isRunning = record.task && ['queued', 'running'].includes(record.task.state);

  return <section className="screenplay-references" aria-label="Original image matches">
    <div className="character-film-actions">
      {dirty && (
        <button type="button" disabled={disabled || !dirty || !!invalid} title={invalid || (!dirty ? 'Selections are saved' : undefined)} onClick={onSave}>
          <Save size={16} />Save image selections
        </button>
      )}
      {!hasPlan && (
        <button type="button" className="character-film-primary" disabled={!!prepareReason} title={prepareReason || 'Bind selected originals and prepare the plan with Planner (Codex / Gemini)'} onClick={onPrepare}>
          <Upload size={16} />Prepare screenplay plan
        </button>
      )}
      {isPaused && onResume && (
        <button type="button" className="character-film-primary" disabled={!!resumeReason} title={resumeReason || 'Resume pipeline'} onClick={onResume}>
          <Play size={16} />Resume pipeline{record.task?.step ? ` (${record.task.step})` : ''}
        </button>
      )}
      {isRunning && onPause && (
        <button type="button" disabled={!!pauseReason} title={pauseReason || 'Pause pipeline'} onClick={onPause}>
          <Pause size={16} />Pause pipeline
        </button>
      )}
      {hasPlan && record.status === 'review' && onNavigate && (
        <button type="button" className="character-film-primary" onClick={() => onNavigate('plan')}>
          <ArrowRight size={16} />Next: Review Plan
        </button>
      )}
      {hasPlan && record.status === 'approved' && !record.task && onRun && (
        <button type="button" className="character-film-primary" disabled={!!runReason} title={runReason || 'Generate selected episodes'} onClick={onRun}>
          <Play size={16} />Generate selected episodes
        </button>
      )}
      {record.task?.kind === 'run' && onNavigate && (
        <button type="button" onClick={() => onNavigate('progress')}>
          <ArrowRight size={16} />View Progress
        </button>
      )}
    </div>
    {dirty && <p role="status">Unsaved image selections</p>}
    {dirty && invalid && <p className="character-film-error" role="alert">{invalid}</p>}
    {!hasPlan && prepareReason && <p className="character-film-muted" role="status">{prepareReason}</p>}
    {isPaused && resumeReason && <p className="character-film-muted" role="status">{resumeReason}</p>}

    {(hasPlan || !!record.execution || !!record.task) && <ScreenplayProgressReferences record={record} />}

    {hasPlan ? (
      <details className="screenplay-details" open={!record.execution}>
        <summary>Original image matches & selections ({(record.matches ?? []).length} items)</summary>
        {(record.matches ?? []).map(match => <ScreenplayMatchChoices key={`${record.id}:${match.kind}:${match.name}`}
          record={record} match={match} selections={selections} disabled={disabled} onChange={onChange} />)}
        {record.analysis?.characters.filter(character => character.voiceOnly).map(character => <p key={character.name}>
          <strong>{character.name}</strong> / voice only / {character.description}</p>)}
        <div className="character-film-actions">
          <button type="button" disabled={disabled || !dirty || !!invalid} title={invalid || (!dirty ? 'Selections are saved' : undefined)} onClick={onSave}>
            <Save size={16} />Save image selections
          </button>
        </div>
      </details>
    ) : (
      <>
        {(record.matches ?? []).map(match => <ScreenplayMatchChoices key={`${record.id}:${match.kind}:${match.name}`}
          record={record} match={match} selections={selections} disabled={disabled} onChange={onChange} />)}
        {record.analysis?.characters.filter(character => character.voiceOnly).map(character => <p key={character.name}>
          <strong>{character.name}</strong> / voice only / {character.description}</p>)}
      </>
    )}

    <h3>Episode assignments</h3>
    <ul className="screenplay-assignment-list">{record.analysis?.episodes.map(episode => <li key={episode.number}>
      <strong>Episode {episode.number}</strong><span>{screenplayEpisodeProductNames(episode).join(', ')}</span><span>{episode.durationSec}s target</span>
    </li>)}</ul>
  </section>;
}